import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { FiEdit2 } from "react-icons/fi";
import { MdDeleteForever } from "react-icons/md";
// import { TiPrinter } from "react-icons/ti";
import { useDispatch, useSelector } from "react-redux";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";

import SearchFilter from "../../Common/SearchFilter";
import ColumnVisibilityControl from "../../Common/ColumnVisibilityControl";
import ActionButton from "../../Common/ActionButton";
import DeleteConfirmModal from "../../../Common/DeleteConfirmModal";
import { getallMaterial } from "../../../Redux/actions/Operationsactions/getmaterial";
import { DeleteMaterialApi } from "../../../Api/Store/Material";

/**
 * Stocks List Component – matches the theme used across other list pages
 * (Vendors, Sellers, etc.).  Backend is not wired yet; rowData defaults to [].
 */
const StocksTable = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const materialData = useSelector((state) => state.getAllMaterial);
  const rolesData = useSelector((state) => state.GetAllRole);

  const gridRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [columnVisibility, setColumnVisibility] = useState({});
  const [filteredMaterials, setFilteredMaterials] = useState([]);

  const [selectedStock, setSelectedStock] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    dispatch(getallMaterial());
  }, [dispatch]);

  useEffect(() => {
    const allData = materialData?.data || [];
    const filtered = allData.filter((item) => {
      const matchesSearch =
        !searchTerm ||
        [
          (item?.CategoryName || "").toLowerCase(),
          (item?.ItemName || "").toLowerCase(),
          (item?.HsCode || "").toLowerCase(),
          (item?.UomName || "").toLowerCase(),
          (item?.ItemDescription || item?.Note || "").toLowerCase(),
        ].some((field) => field.includes(searchTerm.toLowerCase()));
      return matchesSearch;
    });
    setFilteredMaterials(filtered);
  }, [searchTerm, materialData?.data]);

  const handleConfirmDelete = async () => {
    if (!selectedStock) {
      return;
    }
    setIsDeleting(true);
    try {
      const res = await DeleteMaterialApi(selectedStock?.Id);
      if (res?.data?.IsSuccess) {
        toast.success("Material deleted successfully");
        dispatch(getallMaterial());
      } else {
        toast.error(res?.data?.Message || "Failed to delete material");
      }
    } catch (err) {
      toast.error("Something went wrong while deleting");
    } finally {
      setIsDeleting(false);
      setSelectedStock(null);
    }
  };

  /* ─── Column Definitions ──────────────────────────────────────────────── */
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Category",
        field: "CategoryName",
        flex: 1,
      },
      {
        headerName: "Name",
        field: "ItemName",
        flex: 1,
      },
      // {
      //   headerName: "HS Code",
      //   field: "HsCode",
      //   flex: 1,
      // },
      {
        headerName: "UOM",
        field: "UomName",
        flex: 1,
      },
      // {
      //   headerName: "UOM Consumption",
      //   field: "UOMConsumption",
      //   flex: 1,
      // },
      {
        headerName: "Restock Level",
        field: "RestockLevel",
        flex: 1,
      },
      {
        headerName: "Note",
        field: "ItemDescription",
        flex: 1,
      },
      {
        headerName: "Actions",
        colId: "actions",
        width: 130,
        filter: false,
        sortable: false,
        suppressMenu: true,
        headerComponent: () => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            {rolesData?.data?.UserRoles?.includes("Store Material_Create") ? (
              <ActionButton
                text="New+"
                newBtn="true"
                onClick={() => history.push("/store/material/add")}
              />
            ) : (
              <span>Action</span>
            )}
          </div>
        ),
        cellRenderer: (params) => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              gap: "12px",
            }}
          >
            {rolesData?.data?.UserRoles?.includes("Store Material_Edit") && (
              <FiEdit2
                title="Edit"
                size="1.1em"
                style={{ cursor: "pointer", color: "#4e545a" }}
                onClick={(e) => {
                  e.stopPropagation();
                  history.push("/store/material/add", { ...params.data, isEdit: true });
                }}
              />
            )}
            {rolesData?.data?.UserRoles?.includes("Store Material_Delete") && (
              <MdDeleteForever
                title="Delete"
                size="1.1em"
                className="cur_sor"
                style={{ cursor: "pointer", color: "#c0392b" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedStock(params.data);
                }}
              />
            )}
          </div>
        ),
      },
    ],
    [history, rolesData],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (p) => p.value,
      valueFormatter: (p) =>
        p.value === null || p.value === undefined || p.value === "" ? "N/A" : p.value,
      minWidth: 100,
    }),
    [],
  );

  /* ─── Column show / hide ──────────────────────────────────────────────── */
  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({ ...prev, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  /* ─── Render ──────────────────────────────────────────────────────────── */
  return (
    <>
      {/* Toolbar */}
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: "12px", width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Material"
        >
          Material
        </div>
        <SearchFilter
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search"
        />

        <ColumnVisibilityControl
          gridApi={gridRef.current?.api}
          columnVisibility={columnVisibility}
          toggleColumn={toggleColumn}
        />
        {materialData?.loading && (
          <div>
            <ThreeDots color="green" height={40} width={40} />
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div className="ag-theme-alpine vouchers_table_height3 mt-3" style={{ width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredMaterials || []}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              quickFilterText={searchTerm}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
            />
          </div>
        </div>

        {/* Export buttons */}
        {/* <div className="col-12 mt-1 pr-0 pl-0">
          {rolesData?.data?.UserRoles?.includes("Settings_Export") && (
            <>
              <span className="float-left mt-1">
                <ActionButton
                  text="Print"
                  icon={TiPrinter}
                  isPrint={true}
                  newBtn="true"
                  onClick={() => window.print()}
                />
              </span>
              <span className="float-left mt-1">
                <ActionButton
                  text="Download CSV"
                  isPrint={true}
                  newBtn="true"
                  onClick={() => {
                    if (gridRef.current?.api) {
                      const columnKeys = gridRef.current.api
                        .getColumns()
                        ?.map((col) => col.getColId())
                        .filter((id) => id !== "actions");
                      gridRef.current.api.exportDataAsCsv({
                        fileName: "Stocks.csv",
                        columnKeys,
                      });
                    }
                  }}
                />
              </span>
            </>
          )}
        </div> */}
      </div>

      <DeleteConfirmModal
        open={!!selectedStock}
        onClose={() => setSelectedStock(null)}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        message={`Are you sure you want to delete stock "${selectedStock?.ItemName}"?`}
        heading="Delete Stock"
      />
    </>
  );
};

export { StocksTable };
export default StocksTable;
